<template>
  <div class="flex flex-col gap-6" data-testid="page-booking-overview">
    <h2 class="txt-headline-2xl">{{ $t('FestivalShop.bookingOverview.title') }}</h2>
    <div
      v-if="SHOW_OVERVIEW_BANNER"
      class="flex h-[126px] w-full items-center justify-between rounded-lg bg-surface p-6 text-main dark-theme"
    >
      <div class="min-w-[200px] shrink-0"></div>
      <div class="flex max-w-[300px] flex-col gap-2">
        <div class="flex justify-center">
          <ChipBadge color="purple" rounded size="m" type="solid">
            {{ $t('FestivalShop.bookingOverview.banner.slotsLeft', { count: slotsLeft }) }}
          </ChipBadge>
        </div>
        <h3 class="txt-headline-xs text-center">{{ $t('FestivalShop.bookingOverview.banner.headline') }}</h3>
      </div>
      <div class="min-w-[200px] shrink-0 text-right">
        <button class="btn-outline-neutral-s mr-12.5">{{ $t('FestivalShop.bookingOverview.banner.cta') }}</button>
      </div>
    </div>

    <div class="grid gap-3 tablet:grid-cols-2">
      <CategoryCard v-for="cat in productCategories" :key="cat.id" :category="cat" />
    </div>
    <ProductCategoryFormLink
      v-if="oscUrl"
      :title="$t('FestivalShop.bookingOverview.venueServices.title')"
      :copy="$t('FestivalShop.bookingOverview.venueServices.copy')"
      :cta="$t('FestivalShop.bookingOverview.venueServices.cta')"
      :url="oscUrl"
    />
    <ProductCategoryFormLink
      v-if="staffPlanningUrl"
      :title="$t('FestivalShop.bookingOverview.staffPlanning.title')"
      :copy="$t('FestivalShop.bookingOverview.staffPlanning.copy')"
      :cta="$t('FestivalShop.bookingOverview.staffPlanning.cta')"
      :url="staffPlanningUrl"
    />
    <ProductCategoryFormLink
      v-if="reusablesUrl && festivalShopConfig.showReusableCategory"
      :title="$t('FestivalShop.bookingOverview.reusables.title')"
      :copy="$t('FestivalShop.bookingOverview.reusables.copy')"
      :cta="$t('FestivalShop.bookingOverview.reusables.cta')"
      :url="reusablesUrl"
    />
  </div>
</template>
<script lang="ts" setup>
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import CategoryCard from '@/components/FestivalShop/CategoryCard.vue'
import ProductCategoryFormLink from '@/components/FestivalShop/ProductCategoryFormLink.vue'
import { festivalShopConfig } from '@/config/festivalShop'
import { BoothTypeEnum, type EventBySlugQuery, type KioskUnion, type ProductCategory } from '@/gql/myomr'

const SHOW_OVERVIEW_BANNER = false
const slotsLeft = 14
const STAFF_PLANNING_URL_DE = 'https://services.omr.com/omr-2026-event-personal'
const STAFF_PLANNING_URL_EN = 'https://services.omr.com/omr-2026-event-personal/en'

const props = defineProps<{
  event: EventBySlugQuery['eventBySlug']
  productCategories: ProductCategory[]
  kioskId: string
  kiosk: KioskUnion
}>()
const { locale } = useI18n()

const oscUrl = computed(() => {
  if (props.kiosk.__typename !== 'Booth') return null

  const allowedBoothTypes = [
    BoothTypeEnum.CreatorLoungeBooth,
    BoothTypeEnum.PremiumOutdoorBooth,
    BoothTypeEnum.PremiumBooth,
    BoothTypeEnum.BrandedRoomBooth,
    BoothTypeEnum.PremiumExpoTurnkeyBooth,
    BoothTypeEnum.MeetingRoomBooth,
  ]

  if (!props.kiosk.type || !allowedBoothTypes.includes(props.kiosk.type)) return null
  return props.kiosk.oscPortalUrl
})

const reusablesUrl = computed(() => {
  if (props.kiosk.__typename !== 'Booth' || !props.kiosk.type) return null

  if ([BoothTypeEnum.StartupBooth, BoothTypeEnum.SystemBooth].includes(props.kiosk.type)) {
    return 'https://manage.omr.com/content/content-gastro_MC_expo'
  }

  if (
    [
      BoothTypeEnum.PremiumExpoTurnkeyBooth,
      BoothTypeEnum.PremiumBooth,
      BoothTypeEnum.BrandedRoomBooth,
      BoothTypeEnum.CoExhibitorBooth,
    ].includes(props.kiosk.type)
  ) {
    return 'https://manage.omr.com/content/content-gastro-MCC'
  }

  return null
})

const staffPlanningUrl = computed(() => {
  if (props.kiosk.__typename !== 'Booth') return null
  return locale.value === 'en' ? STAFF_PLANNING_URL_EN : STAFF_PLANNING_URL_DE
})
</script>
